/**
 * PremiumPlateInput.jsx
 * Input de Placa de Veículo com validação automática brasileira
 *
 * Suporta:
 * - Placa antiga: ABC-1234
 * - Placa Mercosul: ABC1D23
 * - Formatação automática
 * - Validação em tempo real
 * - Máscara automática
 */

import React, { useState, useCallback, forwardRef } from 'react';
import { InputAdornment } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PremiumInput from './PremiumInput';

// Regex para validação de placas
const PLATE_REGEX_OLD = /^[A-Z]{3}-?\d{4}$/;
const PLATE_REGEX_MERCOSUL = /^[A-Z]{3}\d[A-Z]\d{2}$/;

/**
 * Formata a placa automaticamente
 * - Detecta formato antigo (ABC-1234) ou Mercosul (ABC1D23)
 */
const formatPlate = (value) => {
  if (!value) return '';
  const clean = value.toUpperCase().replace(/[^A-Z0-9]/g, '');

  if (clean.length === 0) return '';

  // Detectar se é formato antigo (3 letras + 4 números)
  const isOldFormat = clean.length >= 4 && /^[A-Z]{3}\d/.test(clean) && !/^[A-Z]{3}\d[A-Z]/.test(clean);

  if (isOldFormat) {
    // Formato antigo: ABC-1234
    const letters = clean.slice(0, 3);
    const numbers = clean.slice(3, 7).replace(/[^0-9]/g, '');
    if (numbers.length > 0) {
      return `${letters}-${numbers}`;
    }
    return letters;
  }

  // Formato Mercosul: ABC1D23 (máximo 7 caracteres)
  return clean.slice(0, 7);
};

/**
 * Valida a placa
 */
const validatePlate = (value) => {
  if (!value) return { valid: null, message: '' };

  const formatted = value.replace(/-/g, '');
  const clean = formatted.toUpperCase().replace(/[^A-Z0-9]/g, '');

  // Verifica formato antigo
  if (PLATE_REGEX_OLD.test(value)) {
    return { valid: true, message: 'Placa válida (formato antigo)' };
  }

  // Verifica formato Mercosul
  if (PLATE_REGEX_MERCOSUL.test(clean)) {
    return { valid: true, message: 'Placa válida (Mercosul)' };
  }

  // Ainda digitando
  if (clean.length < 7) {
    return { valid: null, message: `${clean.length}/7 caracteres` };
  }

  // Formato inválido
  return { valid: false, message: 'Formato inválido' };
};

/**
 * PremiumPlateInput Component
 */
const PremiumPlateInput = forwardRef(({
  value,
  onChange,
  label = 'Placa',
  showIcon = true,
  ...props
}, ref) => {
  const [validation, setValidation] = useState({ valid: null, message: '' });

  const handleChange = useCallback((e) => {
    const formatted = formatPlate(e.target.value);
    const result = validatePlate(formatted);
    setValidation(result);
    onChange?.({ target: { value: formatted, name: e.target.name } });
  }, [onChange]);

  const getHelperTextColor = () => {
    if (validation.valid === true) return 'success.main';
    if (validation.valid === false) return 'error.main';
    return 'text.secondary';
  };

  return (
    <PremiumInput
      ref={ref}
      {...props}
      value={value || ''}
      onChange={handleChange}
      label={label}
      placeholder="ABC-1234 ou ABC1D23"
      inputProps={{
        maxLength: 8,
        style: { textTransform: 'uppercase', letterSpacing: '0.05em' },
        ...props.inputProps,
      }}
      InputProps={{
        startAdornment: showIcon ? (
          <InputAdornment position="start">
            <DirectionsCarIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          </InputAdornment>
        ) : undefined,
        endAdornment: validation.valid === true ? (
          <InputAdornment position="end">
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
          </InputAdornment>
        ) : undefined,
        ...props.InputProps,
      }}
      helperText={validation.message}
      FormHelperTextProps={{
        sx: { color: getHelperTextColor() },
      }}
      error={validation.valid === false}
      success={validation.valid === true}
    />
  );
});

PremiumPlateInput.displayName = 'PremiumPlateInput';

export default PremiumPlateInput;
